import { useEffect, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { CommonSelect, RequiredFieldAstrick } from "components/Common/Common";

// css
import styles from "./addForm.module.scss";
import { FieldArray, Formik } from "formik";
import addHomeEducationSchema from "./addForm.schema";
import AppInputField from "components/shared/FormComponents/AppInputField";
import AppSelectField from "components/shared/FormComponents/AppSelectField";
import AppFileUploadField from "components/shared/FormComponents/AppFileUploadField";
import { MIME_TYPES } from "config/constants";
import { cloneDeep } from "lodash";
import { useCreateHomeEducationClientMutation, useGetHomeEducationClientDetailsQuery, useUpdateHomeEducationClientMutation } from "store/services/clients/homeEducation.client";
import { MinusIcon, PlusIcon } from "Assets/icons";
import { successMessageToast } from "helpers/common";
import { generatePath, useNavigate, useParams, Link } from "react-router-dom";
import { HOME_EDUCATION_LIST, VIEW_HOME_EDUCATION } from "config/paths";
import LoadingIndicator from "components/shared/LoadingIndicator";
import { GENDER_OPTIONS, GRADE_OPTIONS } from "config/constants";
import { useClientsServiceStatusListQuery } from "store/services/utility";
import AppDatePicker from "components/shared/FormComponents/AppDatePicker";
import { useCreateNoteMutation, useGetNoteQuery } from "store/services/note";
import NotesComponent from "components/shared/FormComponents/NotesComponent";
import moment from "moment";
import { useArchiveGuardianMutation } from "store/services/archive";
import ConfirmationPop from "components/Modals/ConfirmationPop";
import LoadingOverlay from "components/shared/LoadingOverlay";
import GuardianPicker from "components/shared/Cllients/GuardianPicker";
import DuplicateClientModal from "components/Modals/DuplicateClientModal";
import { useCheckDuplicateClientMutation } from "store/services/client";

const AddClientHome = () => {

  const { id: clientId } = useParams();

  const editMode = !!clientId;
  const navigate = useNavigate();
  const [currentEntry, setCurrentEntry] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [initialValues, setInitialValues] = useState({
    lastname: "",
    firstname: "",
    dob: "",
    gender: "",
    grade: "",
    city_town: "",
    education_authority: "",
    intake_date: null,
    status: "active",
    documents: [],
    note: "",
    parents: [{
      firstname: "",
      lastname: "",
      phone: "",
      email: ""
    }],
    contracts: [],
    // service_status: ""
  });

  //  Service status lookup
  const { data: serviceStatusList, isLoading: serviceStatusListLoading } = useClientsServiceStatusListQuery({ module: "client_service", client_type: "home_education" });
  const serviceStatusOptions = serviceStatusList?.map(i => ({ value: i.id, label: i.title }));

  // Notes
  const [createNote, { isLoading: createNoteLoading }] = useCreateNoteMutation();
  const { data: noteDetails, isLoading: getNoteLoading } = useGetNoteQuery({ clientId, moduleName: "clients-home-education" }, { skip: !clientId });

  const [createClient, { isLoading: createClientLoading }] = useCreateHomeEducationClientMutation();
  const [updateClient, { isLoading: updateClientLoading }] = useUpdateHomeEducationClientMutation();
  const [archiveGuardian, { isLoading: archiveGuardianLoading }] = useArchiveGuardianMutation();
  const [checkDuplicateClient, { isLoading: duplicateCheckLoading }] = useCheckDuplicateClientMutation();
  const [duplicateMatches, setDuplicateMatches] = useState([]);
  const [pendingClientPayload, setPendingClientPayload] = useState(null);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);

  const { data: clientDetails, isLoading } = clientId ? useGetHomeEducationClientDetailsQuery(clientId) : {};
  const parents = clientDetails?.client_detail?.client_guardian_mapping?.map((guardian) => guardian?.client_guardian?.client_role?.slug == "parent" ? guardian?.client_guardian?.id : null)?.filter(Boolean);

  const handleGuardianArchive = async () => {

    try {
      setShowModal(false);

      const list = cloneDeep(currentEntry?.list || []);
      const index = list.findIndex(x => x.id == currentEntry?.id);
      if (index > -1) {

        delete list[index];
        typeof currentEntry?.setFieldValue == "function" && currentEntry?.setFieldValue("parents", list?.filter(Boolean));

        const enterToDelete = clientDetails?.client_detail?.client_guardian_mapping?.find(x => x?.client_guardian?.id == currentEntry?.id);
        const result = await archiveGuardian(enterToDelete?.id);

        if (result?.error) return;
        successMessageToast(result?.data?.message);
      }

    } catch (err) {
      console.error("Error archive guardian", err);
    }
  };

  const createOrUpdateClient = async (payload) => {

    try {
      let result;
      let values = cloneDeep(payload);

      delete values.note;

      // format values
      values.gender = payload?.gender?.value;
      values.grade = payload?.grade?.value;
      values.school_division = payload?.school_division?.value;
      values.school_profile = payload?.school_profile?.value;
      values.intake_date = payload?.intake_date || null;
      // values.service_status = payload?.service_status?.value;

      values.parents = values.parents?.map(row => ({
        ...row,
        lastname: row.lastname.label
      }))

      if (editMode) {
        result = await updateClient({ data: values, clientId });
      } else {
        result = await createClient(values);
      }

      if (result?.error) {
        console.error("Error creating School Client", result.error);
      }
      else {

        const data = { client_id: result?.data?.data?.id, module: "clients-home-education", note: payload.note }
        if (payload?.note) await createNote(data)

        successMessageToast(result?.data?.message);
        navigate(HOME_EDUCATION_LIST);
      }
    } catch (err) {
      console.error("Error creating School Client", err);
    }
  };

  const handleSubmit = async (payload) => {
    if (editMode) {
      await createOrUpdateClient(payload);
      return;
    }

    try {
      const duplicateResult = await checkDuplicateClient({
        clientType: "home_education",
        data: {
          firstname: payload?.firstname?.trim(),
          lastname: payload?.lastname?.trim(),
          dob: moment(payload?.dob).format("YYYY-MM-DD"),
        },
      }).unwrap();
      const matches = duplicateResult?.data?.matches || [];

      if (matches.length) {
        setDuplicateMatches(matches);
        setPendingClientPayload(cloneDeep(payload));
        setShowDuplicateModal(true);
        return;
      }

      await createOrUpdateClient(payload);
    } catch (err) {
      console.error("Error checking for duplicate Home Education Client", err);
    }
  };

  const handleCreateAnyway = async () => {
    if (!pendingClientPayload) return;

    setShowDuplicateModal(false);
    await createOrUpdateClient(pendingClientPayload);
    setPendingClientPayload(null);
  };

  const handleViewDuplicateClient = (id) => {
    setShowDuplicateModal(false);
    setPendingClientPayload(null);
    navigate(generatePath(VIEW_HOME_EDUCATION, { id }));
  };

  const handleCancelDuplicate = () => {
    setShowDuplicateModal(false);
    setPendingClientPayload(null);
  };

  const handleAttachDocument = (name, value, prevValues, setFieldValue) => {
    try {
      let cloneValues = cloneDeep(prevValues);
      cloneValues.push({
        attachment_key: value,
        attachment_name: value,
      })
      setFieldValue(name, cloneValues);
    } catch (err) {
      console.error("Failed to attach document", err);
    }
  };

  useEffect(() => {

    if (!clientId) return;

    let parents = [];
    clientDetails?.client_detail?.client_guardian_mapping?.length &&
      clientDetails?.client_detail?.client_guardian_mapping?.forEach((guardian) => {
        let { id, firstname, lastname, phone, email, client_role } = guardian?.client_guardian || {};
        let guardiandetails = {
          id,
          firstname,
          lastname: {
            value: id,
            label: lastname,
          },
          phone,
          email
        };
        if (client_role?.slug == "parent") {
          parents.push(guardiandetails);
        }
      })

    setInitialValues({
      ...initialValues,
      firstname: clientDetails?.firstname,
      lastname: clientDetails?.lastname,
      dob: clientDetails?.client_detail?.dob,
      gender: GENDER_OPTIONS.find(item => item.value === clientDetails?.client_detail?.gender),
      grade: GRADE_OPTIONS.find(item => item.value === clientDetails?.client_detail?.grade),
      city_town: clientDetails?.client_detail?.city_town,
      school_division: {
        value: clientDetails?.client_detail?.client_school_detail?.school_division,
        label: clientDetails?.client_detail?.client_school_detail?.client_school_division?.title,
      },
      school_profile: {
        value: clientDetails?.client_detail?.client_school_detail?.school_profile,
        label: clientDetails?.client_detail?.client_school_detail?.client_school?.name,
      },
      parents: parents?.length ? parents : [singleParent],
      documents:
        clientDetails?.clientResources?.length ?
          clientDetails?.clientResources?.map(resource => resource.type == "attachment" && ({
            id: resource.id,
            attachment_key: resource.attachment_key,
            attachment_name: resource.attachment_name
          }))?.filter(Boolean) : [],

      education_authority: clientDetails?.client_detail?.client_home_education_detail?.education_authority,
      intake_date: moment(clientDetails?.client_detail?.client_home_education_detail?.intake_date).isValid() ? clientDetails?.client_detail?.client_home_education_detail?.intake_date : "",
      status: clientDetails?.status,
      // service_status: serviceStatusOptions?.find(x => x.value == clientDetails?.client_detail?.service_status) || "",

    });

  }, [clientDetails]);

  return (
    <>
      <ConfirmationPop
        confirmation={showModal}
        setConfirmation={setShowModal}
        type={"delete"}
        confirmHandler={handleGuardianArchive}
      />
      <DuplicateClientModal
        show={showDuplicateModal}
        matches={duplicateMatches}
        onCancel={handleCancelDuplicate}
        onCreateAnyway={handleCreateAnyway}
        onViewClient={handleViewDuplicateClient}
        isCreating={createClientLoading}
      />
      <section className={`${styles.addForm} position-relative py-4 addForm`}>
        <Container fluid={true}>
          <Row>
            <Col lg="12" className="my-2">
              <div className="pageHead pb-3 d-flex align-items-center">
                <Link
                  className="themeBlue fw-sbold back-to-btn"
                  to="/client/home-education/"
                >
                  <svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 12L0 6L6 0L7.425 1.4L2.825 6L7.425 10.6L6 12ZM18 14V10C18 9.16667 17.7083 8.45833 17.125 7.875C16.5417 7.29167 15.8333 7 15 7H8.825L12.425 10.6L11 12L5 6L11 0L12.425 1.4L8.825 5H15C16.3833 5 17.5625 5.4875 18.5375 6.4625C19.5125 7.4375 20 8.61667 20 10V14H18Z" fill="#1C1B1F" />
                  </svg> Back
                </Link>
                <h6 className="m-0 fw-bold themeBlue">{editMode ? "Edit Client" : "Add New Client"}</h6>
              </div>
            </Col>
            {
              isLoading || serviceStatusListLoading ? <LoadingIndicator /> :

                <Col lg="12" className="my-2">
                  {(archiveGuardianLoading || duplicateCheckLoading) && <LoadingOverlay />}

                  <Formik
                    initialValues={initialValues}
                    validationSchema={addHomeEducationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize
                  >
                    {({
                      handleSubmit,
                      isSubmitting,
                      values,
                      errors,
                      setFieldValue,
                    }) => (
                      <Form onSubmit={handleSubmit}>
                        <div
                          className="py-3"
                          style={{ borderTop: "1px solid #41a2a84d" }}
                        >
                          <Row>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Last Name
                                <RequiredFieldAstrick />
                              </label>
                              <AppInputField
                                name="lastname"
                                type="text"
                                placeholder="e.g Smith"
                                className={`${styles.formControl} form-control`}
                                maxLength={30}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                First Name
                                <RequiredFieldAstrick />
                              </label>
                              <AppInputField
                                name="firstname"
                                type="text"
                                placeholder="e.g John"
                                className={`${styles.formControl} form-control`}
                                maxLength={30}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                DOB
                                <RequiredFieldAstrick />
                              </label>
                              <AppDatePicker
                                name="dob"
                                maxDate={new Date()}
                                placeholderText="Select DOB"
                                className={`${styles.formControl} form-control`}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Gender Assigned at Birth
                                <RequiredFieldAstrick />
                              </label>
                              <CommonSelect
                                name="gender"
                                placeholder={"Select Gender"}
                                option={GENDER_OPTIONS}
                              // cstmOption={CustomGenderOption}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Grade
                              </label>
                              <CommonSelect
                                name="grade"
                                placeholder={"Please Select Grade"}
                                option={GRADE_OPTIONS}
                              // cstmOption={CustomGenderOption}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                City/Town
                                <RequiredFieldAstrick />
                              </label>
                              <AppInputField
                                type="text"
                                name="city_town"
                                placeholder="City/Town"
                                className={`${styles.formControl} form-control`}
                                maxLength={30}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Status
                                <RequiredFieldAstrick />
                              </label>
                              <AppSelectField
                                name={"status"}
                                placeholder={"Please Select"}
                                className={`${styles.formControl} form-control`}
                                options={[
                                  {
                                    value: "active",
                                    label: "Active",
                                  },
                                  {
                                    value: "archived",
                                    label: "Archived",
                                  }
                                ]}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Home Education Authority
                              </label>
                              <AppInputField
                                type="text"
                                name="education_authority"
                                placeholder="Please Enter"
                                className={`${styles.formControl} form-control`}
                                maxLength={30}
                              />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Date of Intake
                              </label>
                              <AppDatePicker
                                name="intake_date"
                                className={`${styles.formControl} form-control`}
                              />
                            </Col>
                            {/* <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Service Status
                                <RequiredFieldAstrick />
                              </label>
                              <CommonSelect
                                name="service_status"
                                placeholder={"Please Select Service Status"}
                                option={serviceStatusOptions}
                              />
                            </Col> */}
                            <Col md="12" sm="12" className="my-2">
                              {/* <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex"
                              >
                                Notes
                              </label> */}
                              {
                                editMode ?
                                  <NotesComponent
                                    notes={noteDetails}
                                    className={`${styles.formControl} form-control`}
                                    module={"clients-home-education"}
                                    client_id={clientId}
                                  /> :
                                  <AppInputField
                                    name={`note`}
                                    placeholder="Notes"
                                    fieldType={"textarea"}
                                    maxLength={500}
                                    className={`${styles.formControl} form-control`}
                                  />
                              }
                            </Col>
                            <Col md="12" sm="12" className="my-2">
                              <hr />
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0 d-flex my-2"
                                style={{ fontSize: "15px" }}
                              >
                                Parent / Guardian Details
                              </label>
                              <FieldArray name="parents">
                                {({ insert, remove, push }) => (
                                  <ul className="list-unstyled ps-0 mb-0">
                                    {values.parents.length > 0 &&
                                      values.parents.map((area, index) => {
                                        let isLastItem = index === values.parents.length - 1;
                                        return (
                                          <Row className="align-items-center" key={index}>
                                            <Col lg="10">
                                              <Row>
                                                <Col md="3" sm="6" className="my-2">
                                                  <label
                                                    htmlFor=""
                                                    className="form-label themeBlue fw-bold mb-0 d-flex"
                                                  >
                                                    Last Name
                                                    <RequiredFieldAstrick />
                                                  </label>
                                                  <GuardianPicker
                                                    index={index}
                                                    type="parents"
                                                    name={`parents.${index}.lastname`}
                                                    placeholder={"Search Last Name"}
                                                  />
                                                </Col>
                                                <Col md="3" sm="6" className="my-2">
                                                  <label
                                                    htmlFor=""
                                                    className="form-label themeBlue fw-bold mb-0 d-flex"
                                                  >
                                                    First Name
                                                    <RequiredFieldAstrick />
                                                  </label>
                                                  <AppInputField
                                                    disabled={!!area?.id}
                                                    name={`parents.${index}.firstname`}
                                                    placeholder="e.g John Smith"
                                                    className={`${styles.formControl} form-control`}
                                                  />
                                                </Col>
                                                <Col md="3" sm="6" className="my-2">
                                                  <label
                                                    htmlFor=""
                                                    className="form-label themeBlue fw-bold mb-0 d-flex"
                                                  >
                                                    Email
                                                    <RequiredFieldAstrick />
                                                  </label>
                                                  <AppInputField
                                                    disabled={!!area?.id}
                                                    name={`parents.${index}.email`}
                                                    placeholder="John@gmail.com"
                                                    className={`${styles.formControl} form-control`}
                                                  />
                                                </Col>
                                                <Col md="3" sm="6" className="my-2">
                                                  <label
                                                    htmlFor=""
                                                    className="form-label themeBlue fw-bold mb-0 d-flex"
                                                  >
                                                    Phone
                                                    <RequiredFieldAstrick />
                                                  </label>
                                                  <AppInputField
                                                    disabled={!!area?.id}
                                                    name={`parents.${index}.phone`}
                                                    placeholder="+1234567890"
                                                    className={`${styles.formControl} form-control`}
                                                    maxLength={25}
                                                  />
                                                </Col>
                                              </Row>
                                            </Col>
                                            <Col lg="2">
                                              <Button
                                                className="border-0 p-0"
                                                variant="transparnet"
                                              >
                                                {isLastItem ? (

                                                  values.parents.length > 1 ? (
                                                    <>
                                                      <PlusIcon onClick={() => push({ ...singleParent })} />
                                                      <MinusIcon onClick={() => {
                                                        if (area.id && parents.includes(area.id)) {
                                                          setCurrentEntry({ ...area, setFieldValue, list: values?.parents });
                                                          setShowModal(true);
                                                          return;
                                                        }
                                                        remove(index)
                                                      }} />
                                                    </>
                                                  ) : <PlusIcon onClick={() => push({ ...singleParent })} />

                                                ) : <MinusIcon onClick={() => {
                                                  if (area.id && parents.includes(area.id)) {
                                                    setCurrentEntry({ ...area, setFieldValue, list: values?.parents });
                                                    setShowModal(true);
                                                    return;
                                                  }
                                                  remove(index)
                                                }} />
                                                }
                                              </Button>
                                            </Col>
                                          </Row>
                                        )
                                      })}
                                  </ul>
                                )}
                              </FieldArray>
                              <hr />
                            </Col>
                            <Col md="4" sm="6" className="my-2">
                              <label
                                htmlFor=""
                                className="form-label themeBlue fw-bold mb-0"
                              >
                                Attach Documents
                              </label>
                              <AppFileUploadField
                                name="documents"
                                acl={"private"}
                                accept="image/*, application/pdf, .doc, .docx"
                                allowedTypes={[MIME_TYPES.pdf, MIME_TYPES.png, MIME_TYPES.jpeg, MIME_TYPES.jpg, MIME_TYPES.webp, MIME_TYPES.doc, MIME_TYPES.docx]}
                                className={`${styles.formControl} form-control`}
                                onChange={(name, value) => {
                                  handleAttachDocument(name, value, values?.documents, setFieldValue);
                                }}
                              />
                            </Col>
                          </Row>
                        </div>

                        <Row className="mt-4">
                          <Col className="my-2" lg="12">
                            <div
                              className={`${styles.btnWrpper} btnWrpper d-flex align-items-center gap-10 pt-3`}
                              style={{ borderTop: "1px solid #41a2a84d" }}
                            >
                              <Button disabled={createClientLoading || updateClientLoading || createNoteLoading} type="submit" className="d-flex align-items-center justify-content-center commonBtn">
                                {createClientLoading || updateClientLoading || createNoteLoading ? <LoadingIndicator inline /> : "Save"}
                              </Button>
                              <Button
                                type="button"
                                className="d-flex align-items-center justify-content-center commonBtn borderedBtn bg-transparent"
                                onClick={() => navigate(HOME_EDUCATION_LIST)}
                              >
                                Cancel
                              </Button>
                            </div>
                          </Col>
                        </Row>
                      </Form>
                    )}
                  </Formik>
                </Col>
            }

          </Row>
        </Container>
      </section>
    </>
  );
};

const singleParent = {
  firstname: "",
  lastname: "",
  phone: "",
  email: ""
}

export default AddClientHome;
